import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatCalendar } from '@angular/material/datepicker';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, switchMap, of, tap, catchError, Observable, map, finalize } from 'rxjs';
import {
    ScheduledActivity,
    ActivityType,
    CalendarEvent,
    ActivityFilters,
    ActivityStatus,
    ExportFilters,
    CalendarViewOptions
} from '../../interfaces/activity-schedule.interface';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { ActivityNotificationService, ActivityNotification } from '../../services/activity-notification.service';
import { NotificationService } from 'src/app/services/notification.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { AuthService } from 'src/app/services/auth.service';
import { DialogService } from 'src/app/services/dialog.service';
import { CommonService } from 'src/app/services/common.service';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import Swal from 'sweetalert2';

// Import calendar components (will be created)
import { ActivityBookingComponent } from '../activity-booking/activity-booking.component';
import { Generic } from 'src/app/interfaces/generic.interface';

@Component({
    selector: 'app-programari',
    templateUrl: './programari.component.html',
    styleUrls: ['./programari.component.scss']
})
export class ProgramariComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    // Calendar configuration
    @ViewChild('calendar') calendar!: MatCalendar<Date>;

    // Current view state
    currentView: 'month' | 'week' | 'day' | 'agenda' = 'month';
    currentDate = new Date();
    selectedDate = new Date();

    // Data
    activities: ScheduledActivity[] = [];
    calendarEvents: CalendarEvent[] = [];
    activityTypes: ActivityType[] = [];
    filteredActivities: ScheduledActivity[] = [];

    // UI state
    isLoading = false;
    showFilters = false;
    showLegend = true;
    showActivityDetails = false;
    selectedActivityForDetails: ScheduledActivity | null = null;
    isEditingActivity = false;
    preventAutoRefresh = false; // Prevents auto-refresh when user is actively viewing/editing

    // Day activities sidebar state
    showDayActivities = false;
    selectedDateActivities: ScheduledActivity[] = [];
    selectedDateForSidebar: Date | null = null;

    // User context
    currentUserId = 0;
    userPermissions: any = {};

    // Filters
    filtersForm!: FormGroup;
    dateRange = {
        start: new Date(),
        end: new Date()
    };

    // Status options
    statusOptions = this.activityScheduleService.getStatusOptions();

    // View options for calendar
    viewOptions = [
        { value: 'month', label: 'Lună', icon: 'calendar_view_month' },
        { value: 'week', label: 'Săptămână', icon: 'calendar_view_week' },
        { value: 'day', label: 'Zi', icon: 'calendar_today' },
        { value: 'agenda', label: 'Agendă', icon: 'list' }
    ];

    // Notifications
    notifications: ActivityNotification[] = [];
    unreadNotificationsCount = 0;
    showNotificationsPanel = false;

    // Calendar data
    weekDays = ['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata', 'Duminica'];
    calendarWeeks: any[][] = [];
    timeSlots = Array.from({ length: 12 }, (_, i) => i + 8); // 8:00 to 19:00

    // Week view specific data
    weekTimeSlots = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];
    currentWeekStart: Date = new Date();
    weekActivities: ScheduledActivity[] = [];

    // Day view specific data
    dayTimeSlots = ['06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'];
    dayActivities: ScheduledActivity[] = [];

    // UI states
    isMobile = false;
    searchTerm = '';
    selectedStatusFilters: string[] = [];

    // Organizational filtering
    directii: any[] = [];
    compartimente: any[] = [];
    users: any[] = [];

    filteredDirectii: any[] = [];
    directiiControl = new FormControl();
    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();
    filteredUsers: any[] = [];
    usersControl = new FormControl();

    // Date filtering
    dateFilter = {
        startDate: null as Date | null,
        endDate: null as Date | null
    };

    constructor(
        private activityScheduleService: ActivityScheduleService,
        private activityNotificationService: ActivityNotificationService,
        private dialog: MatDialog,
        private notificationService: NotificationService,
        private permissionsService: PermissionsService,
        private authService: AuthService,
        private dialogService: DialogService,
        private cdr: ChangeDetectorRef,
        private commonService: CommonService,
        private operatoriService: OperatoriService,
        private fb: FormBuilder,
    ) {
        this.initializeDateRange();
        this.initializeFiltersForm();
    }

    ngOnInit(): void {
        console.log('ProgramariComponent: Initializing component...');

        // Initialize with empty data first to show UI
        this.activities = [];
        this.filteredActivities = [];

        // Initialize mobile detection
        this.checkMobileDevice();

        // Initialize calendar with empty data
        this.generateCalendarWeeks();

        // Load user context first, then data
        this.loadUserContext();
        this.loadOrganizationalData();
        this.loadInitialData();
        this.setupSubscriptions();
        this.renderFieldsSearch();

        // Force initial UI update
        setTimeout(() => {
            this.cdr.detectChanges();
            console.log('ProgramariComponent: Component initialized successfully');
        }, 100);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directii;
                    return of(this.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimente;
                    return of(this.compartimente);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.users;
                    return of(this.users);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    // ========================================
    // INITIALIZATION
    // ========================================

    private initializeDateRange(): void {
        const now = new Date();
        this.dateRange.start = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
        this.dateRange.end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
    }

    private initializeFiltersForm(): void {
        this.filtersForm = new FormGroup({
            start_date: new FormControl(this.dateRange.start),
            end_date: new FormControl(this.dateRange.end),
            status: new FormControl([]), // Array of selected statuses
            activity_type_id: new FormControl(null),
            assigned_user_id: new FormControl(null),
            cerere_id: new FormControl(null),
            search_text: new FormControl(''),
            directie_id: new FormControl(-1),
            compartiment_id: new FormControl(-1),
            user_id: new FormControl(-1)
        });

        // Setup form change subscription with debounce
        // this.filtersForm.valueChanges.pipe(
        //     debounceTime(300),
        //     distinctUntilChanged(),
        //     takeUntil(this.destroy$)
        // ).subscribe(() => {
        //     this.applyFilters();
        // });
    }

    private loadUserContext(): void {
        const user = this.authService.getCurrentUser();
        console.log('ProgramariComponent: Loading user context, user object:', user);

        if (user) {
            // Handle both Promise and direct object returns
            if (user instanceof Promise) {
                user.then(userData => {
                    this.currentUserId = userData?.id_user || userData?.id || 1;
                    this.userPermissions = this.permissionsService.getUserPermissions();
                    console.log('ProgramariComponent: User context loaded from Promise, userId:', this.currentUserId);
                    // Retry loading activities now that we have user ID
                    this.loadCalendarActivities();
                });
            } else {
                this.currentUserId = (user as any)?.id_user || (user as any)?.id || 1;
                this.userPermissions = this.permissionsService.getUserPermissions();
                console.log('ProgramariComponent: User context loaded directly, userId:', this.currentUserId);
                // Retry loading activities now that we have user ID
                this.loadCalendarActivities();
            }
        } else {
            this.currentUserId = 1; // Default fallback
            console.log('ProgramariComponent: No user found, using fallback userId:', this.currentUserId);
            // Load with fallback user ID
            this.loadCalendarActivities();
        }
    }

    private loadInitialData(): void {
        this.isLoading = true;

        // Load activity types
        this.activityScheduleService.getActivityTypes().pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (types) => {
                this.activityTypes = types;
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la încărcarea tipurilor de activități: ' + error.message, 'error');
            }
        });

        // Load calendar activities
        this.loadCalendarActivities();
    }

    private setupSubscriptions(): void {
        // Subscribe to activities updates
        this.activityScheduleService.activities$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(activities => {
            this.activities = activities;
            this.updateCalendarEvents();
            this.applyFilters();
            this.isLoading = false;
        });

        // Subscribe to notifications updates
        this.activityNotificationService.notifications$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(notifications => {
            this.notifications = notifications;
            this.cdr.detectChanges();
        });

        // Subscribe to unread notifications count
        this.activityNotificationService.unreadCount$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(count => {
            this.unreadNotificationsCount = count;
            this.cdr.detectChanges();
        });
    }

    // ========================================
    // DATA LOADING
    // ========================================

    loadCalendarActivities(forceRefresh: boolean = false): void {
        if (!this.currentUserId) {
            console.warn('No user ID available, skipping activity load');
            this.generateCalendarWeeks();
            return;
        }

        // Skip auto-refresh if user is actively viewing/editing and this is not a forced refresh
        if (this.preventAutoRefresh && !forceRefresh) {
            console.log('Skipping auto-refresh while user is viewing/editing activity details');
            return;
        }

        this.isLoading = true;
        const startDate = this.formatDateForAPI(this.dateRange.start);
        const endDate = this.formatDateForAPI(this.dateRange.end);

        console.log('Loading calendar activities for user:', this.currentUserId, 'from', startDate, 'to', endDate);

        this.activityScheduleService.getCalendarActivities(this.currentUserId, startDate, endDate).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (activities) => {
                console.log('Received activities from API:', activities);
                this.activities = Array.isArray(activities) ? activities : [];
                console.log('Processed activities array:', this.activities.length, 'items');
                console.log('Activity details:', this.activities);
                this.updateCalendarEvents();
                this.applyFilters();
                this.isLoading = false;

                // Force regenerate calendar after data load
                setTimeout(() => {
                    console.log('Force regenerating calendar after activity load');
                    this.generateCalendarWeeks();
                }, 100);
            },
            error: (error) => {
                console.error('Error loading calendar activities:', error);
                this.activities = [];
                this.filteredActivities = [];
                this.generateCalendarWeeks();
                this.isLoading = false;
                // Don't show error notification during development to avoid interruptions
                if (error.status !== 0) { // Only show if it's not a dev server connection issue
                    this.notificationService.warningSwal('Eroare', 'Eroare la încărcarea activităților: ' + error.message, 'error');
                }
            }
        });
    }

    private updateCalendarEvents(): void {
        this.calendarEvents = this.activityScheduleService.convertToCalendarEvents(this.activities);
        this.generateCalendarWeeks();
    }

    private generateCalendarWeeks(): void {
        console.log('generateCalendarWeeks: Starting calendar generation with', this.activities.length, 'total activities');

        const weeks = [];
        const firstDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
        const lastDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);

        console.log('generateCalendarWeeks: Month range:', firstDay, 'to', lastDay);

        // Find the Monday of the week containing the first day
        const startDate = new Date(firstDay);
        const day = startDate.getDay();
        const diff = startDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
        startDate.setDate(diff);

        // Generate calendar weeks
        const current = new Date(startDate);
        console.log('generateCalendarWeeks: Calendar starts from:', current);

        for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
            const week = [];
            for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
                const date = new Date(current);
                const activities = this.getActivitiesForDate(date) || [];

                // Sort activities by time
                const sortedActivities = activities.sort((a, b) => {
                    const timeA = a.start_time || '00:00';
                    const timeB = b.start_time || '00:00';
                    return timeA.localeCompare(timeB);
                });

                week.push({
                    date: new Date(date),
                    day: date.getDate(),
                    isCurrentMonth: date.getMonth() === this.currentDate.getMonth(),
                    isToday: this.isToday(date),
                    isSelected: this.isSameDay(date, this.selectedDate),
                    activities: sortedActivities // Show all activities sorted by time
                });

                if (activities.length > 0) {
                    console.log('generateCalendarWeeks: Found', activities.length, 'activities for date', date, activities);
                }

                current.setDate(current.getDate() + 1);
            }
            weeks.push(week);
        }

        this.calendarWeeks = weeks;
        console.log('generateCalendarWeeks: Generated', weeks.length, 'weeks with total calendar cells');
        this.cdr.detectChanges(); // Force change detection
    }

    private isToday(date: Date): boolean {
        const today = new Date();
        return this.isSameDay(date, today);
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }

    // ========================================
    // VIEW MANAGEMENT
    // ========================================

    switchView(view: 'month' | 'week' | 'day' | 'agenda'): void {
        this.currentView = view;
        this.updateDateRangeForView();
        this.loadCalendarActivities();

        // Initialize view-specific data
        switch (view) {
            case 'week':
                this.initializeWeekView();
                break;
            case 'day':
                this.initializeDayView();
                break;
            case 'month':
                this.generateCalendarWeeks();
                break;
        }
    }

    private updateDateRangeForView(): void {
        const current = new Date(this.currentDate);

        switch (this.currentView) {
            case 'day':
                this.dateRange.start = new Date(current);
                this.dateRange.end = new Date(current);
                break;
            case 'week':
                const startOfWeek = new Date(current);
                startOfWeek.setDate(current.getDate() - current.getDay() + 1); // Monday
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
                this.dateRange.start = startOfWeek;
                this.dateRange.end = endOfWeek;
                break;
            case 'month':
            default:
                this.dateRange.start = new Date(current.getFullYear(), current.getMonth(), 1);
                this.dateRange.end = new Date(current.getFullYear(), current.getMonth() + 1, 0);
                break;
        }

        // Update form with new date range
        this.filtersForm.patchValue({
            start_date: this.dateRange.start,
            end_date: this.dateRange.end
        }, { emitEvent: false });
    }

    // Navigation methods
    previousPeriod(): void {
        switch (this.currentView) {
            case 'day':
                this.currentDate.setDate(this.currentDate.getDate() - 1);
                break;
            case 'week':
                this.currentDate.setDate(this.currentDate.getDate() - 7);
                break;
            case 'month':
                this.currentDate.setMonth(this.currentDate.getMonth() - 1);
                break;
        }
        this.updateDateRangeForView();
        this.loadCalendarActivities();
    }

    nextPeriod(): void {
        switch (this.currentView) {
            case 'day':
                this.currentDate.setDate(this.currentDate.getDate() + 1);
                break;
            case 'week':
                this.currentDate.setDate(this.currentDate.getDate() + 7);
                break;
            case 'month':
                this.currentDate.setMonth(this.currentDate.getMonth() + 1);
                break;
        }
        this.updateDateRangeForView();
        this.loadCalendarActivities();
    }

    goToToday(): void {
        this.currentDate = new Date();
        this.selectedDate = new Date();
        this.updateDateRangeForView();
        this.loadCalendarActivities();
    }

    onDateSelected(date: Date): void {
        this.selectedDate = date;
        this.currentDate = new Date(date);
        if (this.currentView !== 'day') {
            this.switchView('day');
        }
    }

    // ========================================
    // FILTERING
    // ========================================

    applyFilters(): void {
        if (!this.filtersForm || !this.activities) {
            this.filteredActivities = [];
            this.generateCalendarWeeks();
            return;
        }

        const formValue = this.filtersForm.value;

        console.log('Applying filters to', this.activities.length, 'activities');

        this.filteredActivities = this.activities.filter(activity => {
            if (!activity) return false;

            // Date range filter (from form)
            try {
                const activityDate = new Date(activity.scheduled_date);
                if (isNaN(activityDate.getTime())) return false;

                if (formValue.start_date && activityDate < formValue.start_date) return false;
                if (formValue.end_date && activityDate > formValue.end_date) return false;
            } catch (e) {
                console.warn('Invalid activity date:', activity.scheduled_date);
                return false;
            }

            // Additional date filter (from filter panel)
            if (this.dateFilter.startDate) {
                const activityDate = new Date(activity.scheduled_date);
                if (activityDate < this.dateFilter.startDate) return false;
            }
            if (this.dateFilter.endDate) {
                const activityDate = new Date(activity.scheduled_date);
                if (activityDate > this.dateFilter.endDate) return false;
            }

            // User filter
            // if (this.selectedUserId && activity.assigned_user_id !== this.selectedUserId) {
            //     return false;
            // }

            // // Directie filter (if user not selected but directie is)
            // if (this.selectedDirectieId && !this.selectedUserId) {
            //     // Check if activity user belongs to selected directie
            //     // This would require additional user data in the activity or a lookup
            //     // For now, we'll skip this filter if no user is selected
            // }

            // // Compartiment filter (if user not selected but compartiment is)
            // if (this.selectedCompartimentId && !this.selectedUserId) {
            //     // Similar to directie filter
            //     // For now, we'll skip this filter if no user is selected
            // }

            // Status filter
            if (formValue.status && formValue.status.length > 0 && !formValue.status.includes(activity.status)) {
                return false;
            }

            // Activity type filter
            if (formValue.activity_type_id && activity.activity_type_id !== formValue.activity_type_id) {
                return false;
            }

            // Assigned user filter
            if (formValue.assigned_user_id && activity.assigned_user_id !== formValue.assigned_user_id) {
                return false;
            }

            // Cerere filter
            if (formValue.cerere_id && activity.cerere_id !== formValue.cerere_id) {
                return false;
            }

            // Search text filter
            if (formValue.search_text && formValue.search_text.trim()) {
                const searchText = formValue.search_text.toLowerCase().trim();
                const searchableText = [
                    activity.title || '',
                    activity.description || '',
                    activity.participants_info || '',
                    activity.location || '',
                    activity.activity_type?.name || '',
                    activity.user?.full_name || ''
                ].join(' ').toLowerCase();

                if (!searchableText.includes(searchText)) {
                    return false;
                }
            }

            return true;
        });

        // Update calendar events with filtered data
        try {
            this.calendarEvents = this.activityScheduleService.convertToCalendarEvents(this.filteredActivities);
        } catch (e) {
            console.warn('Error converting to calendar events:', e);
            this.calendarEvents = [];
        }

        console.log('After filtering:', this.filteredActivities.length, 'activities remain');

        this.generateCalendarWeeks();
        this.cdr.detectChanges();
    }

    clearFilters(): void {
        this.filtersForm.reset({
            start_date: this.dateRange.start,
            end_date: this.dateRange.end,
            status: [],
            activity_type_id: null,
            assigned_user_id: null,
            cerere_id: null,
            search_text: '',
            directie_id: -1,
            compartiment_id: -1,
            user_id: -1,
        });
        this.showFilters = false;
    }

    // ========================================
    // ACTIVITY ACTIONS
    // ========================================

    createNewActivity(suggestedDate?: Date): void {
        const dialogRef = this.dialog.open(ActivityBookingComponent, {
            width: '800px',
            maxHeight: '90vh',
            disableClose: true,
            autoFocus: false,
            data: {
                suggested_date: suggestedDate ? this.formatDateForAPI(suggestedDate) : null,
                suggested_user_id: this.currentUserId
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
                this.loadCalendarActivities(true); // Force refresh after creating activity
                this.notificationService.successSwal('Succes', 'Activitatea a fost creată cu succes!');
            }
        });
    }

    onActivityClick(activity: any, event?: Event): void {
        if (event) {
            event.stopPropagation(); // Prevent day selection
        }
        // If coming from day activities sidebar, switch to single activity view
        if (this.showDayActivities) {
            this.showDayActivities = false;
        }
        this.openActivityDetails(activity as ScheduledActivity);
    }

    onActivityDoubleClick(event: CalendarEvent): void {
        this.editActivity(event.activity);
    }

    openActivityDetails(activity: ScheduledActivity): void {
        console.log('Opening activity details:', activity);
        // Clone the activity object to prevent real-time UI updates during editing
        this.selectedActivityForDetails = JSON.parse(JSON.stringify(activity));
        this.showActivityDetails = true;
        this.isEditingActivity = false;
        this.preventAutoRefresh = true; // Prevent auto-refresh while viewing details
    }

    closeActivityDetails(): void {
        this.showActivityDetails = false;
        this.selectedActivityForDetails = null;
        this.isEditingActivity = false;
        this.showDayActivities = false;
        this.selectedDateActivities = [];
        this.selectedDateForSidebar = null;
        this.preventAutoRefresh = false; // Re-enable auto-refresh when closing details
    }

    startEditingActivity(): void {
        this.isEditingActivity = true;
        this.preventAutoRefresh = true; // Prevent refresh while editing
    }

    saveActivityChanges(updatedActivity: ScheduledActivity): void {
        // Update the activity via the service
        this.activityScheduleService.updateActivityStatus(updatedActivity.id, {
            status: updatedActivity.status,
            notes: updatedActivity.notes || '',
            user_id: this.currentUserId
        }).subscribe({
            next: () => {
                this.closeActivityDetails();
                this.loadCalendarActivities(true); // Force refresh after saving changes
                this.notificationService.successSwal('Succes', 'Activitatea a fost actualizată!');
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la actualizare: ' + error.message, 'error');
            }
        });
    }

    editActivity(activity: ScheduledActivity): void {
        const dialogRef = this.dialog.open(ActivityBookingComponent, {
            width: '800px',
            maxHeight: '90vh',
            disableClose: true,
            autoFocus: false,
            data: {
                mode: 'edit',
                activityId: activity.id,
                prefilledData: activity,
                edit_activity: activity
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
                this.loadCalendarActivities(true); // Force refresh after editing activity
                this.notificationService.successSwal('Succes', 'Activitatea a fost actualizată cu succes!');
            }
        });
    }

    rescheduleActivity(activity: ScheduledActivity): void {
        const dialogRef = this.dialog.open(ActivityBookingComponent, {
            width: '800px',
            maxHeight: '90vh',
            disableClose: true,
            data: {
                mode: 'reschedule',
                activityId: activity.id,
                prefilledData: {
                    ...activity,
                    // Keep all data but suggest new date/time
                    scheduled_date: new Date().toISOString().split('T')[0], // Today's date
                    start_time: activity.start_time // Keep original time as suggestion
                },
                edit_activity: activity
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
                this.loadCalendarActivities(true); // Force refresh after rescheduling
                this.notificationService.successSwal('Succes', 'Activitatea a fost reprogramată cu succes!');
            }
        });
    }

    cancelActivity(activity: ScheduledActivity): void {
        // Use SweetAlert with input field for reason
        Swal.fire({
            title: 'Anulare Activitate',
            text: 'Vă rugăm să specificați motivul anulării acestei activități:',
            input: 'textarea',
            inputPlaceholder: 'Motivul anulării (minim 10 caractere)...',
            inputAttributes: {
                'aria-label': 'Motivul anulării',
                rows: '4',
                maxlength: '500'
            },
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Anulează Activitatea',
            cancelButtonText: 'Păstrează Activitatea',
            confirmButtonColor: '#d33',
            inputValidator: (value) => {
                if (!value || value.trim().length < 10) {
                    return 'Motivul anulării trebuie să aibă cel puțin 10 caractere!';
                }
                return null;
            },
            preConfirm: (inputValue) => {
                if (!inputValue || inputValue.trim().length < 10) {
                    Swal.showValidationMessage('Motivul anulării trebuie să aibă cel puțin 10 caractere!');
                    return false;
                }
                return inputValue.trim();
            }
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                this.performCancellation(activity, result.value);
            }
        });
    }

    private performCancellation(activity: ScheduledActivity, reason: string): void {
        const currentUser = this.authService.getCurrentUser();
        const userId = typeof currentUser === 'object' && currentUser ?
            (currentUser as any).id_user || (currentUser as any).id || this.currentUserId :
            this.currentUserId;
        const userName = typeof currentUser === 'object' && currentUser ?
            (currentUser as any).full_name || (currentUser as any).name || 'Utilizator' :
            'Utilizator';

        const cancellationDate = new Date();
        const formattedCancellationDate = this.formatActivityDateShort(cancellationDate.toISOString().split('T')[0]);
        const formattedCancellationTime = this.formatTime(cancellationDate.toTimeString().split(' ')[0]);

        const cancellationReason = `ANULAT: ${reason}\n\nAnulat de: ${userName} (ID: ${userId})\nData anulării: ${formattedCancellationDate} la ${formattedCancellationTime}`;

        this.activityScheduleService.updateActivityStatus(activity.id, {
            status: 'cancelled',
            reason: reason,
            notes: cancellationReason,
            user_id: this.currentUserId
        }).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: () => {
                this.closeActivityDetails();
                this.loadCalendarActivities(true); // Force refresh after cancellation
                this.notificationService.successSwal('Succes', 'Activitatea a fost anulată cu succes!');
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la anularea activității: ' + error.message, 'error');
            }
        });
    }

    updateActivityStatus(activity: ScheduledActivity, newStatus: ActivityStatus): void {
        this.activityScheduleService.updateActivityStatus(activity.id, {
            status: newStatus,
            user_id: this.currentUserId
        }).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (updatedActivity) => {
                this.loadCalendarActivities(true); // Force refresh after status change
                this.notificationService.successSwal('Succes', `Status-ul activității a fost schimbat în "${this.getStatusLabel(newStatus)}"`);
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la actualizarea status-ului: ' + error.message, 'error');
            }
        });
    }

    // ========================================
    // EXPORT FUNCTIONALITY
    // ========================================

    exportToPDF(): void {
        const filters = this.getExportFilters();

        this.activityScheduleService.exportToPDF(filters, 'list').pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (blob) => {
                const filename = `programari_${this.formatDateForFilename(new Date())}.pdf`;
                this.activityScheduleService.downloadFile(blob, filename);
                this.notificationService.successSwal('Succes', 'Export PDF realizat cu succes!');
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la exportul PDF: ' + error.message, 'error');
            }
        });
    }

    exportToExcel(): void {
        const filters = this.getExportFilters();

        this.activityScheduleService.exportToExcel(filters).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (blob) => {
                const filename = `programari_${this.formatDateForFilename(new Date())}.xlsx`;
                this.activityScheduleService.downloadFile(blob, filename);
                this.notificationService.successSwal('Succes', 'Export Excel realizat cu succes!');
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la exportul Excel: ' + error.message, 'error');
            }
        });
    }

    private getExportFilters(): ExportFilters {
        const formValue = this.filtersForm.value;
        return {
            user_id: this.currentUserId,
            start_date: this.formatDateForAPI(formValue.start_date),
            end_date: this.formatDateForAPI(formValue.end_date),
            status: formValue.status && formValue.status.length > 0 ? formValue.status : undefined,
            activity_type_id: formValue.activity_type_id || undefined,
            cerere_id: formValue.cerere_id || undefined,
        };
    }

    // ========================================
    // UTILITY METHODS
    // ========================================

    private formatDateForFilename(date: Date): string {
        return date.toISOString().split('T')[0].replace(/-/g, '_'); // YYYY_MM_DD
    }

    getStatusLabel(status: ActivityStatus): string {
        const option = this.statusOptions.find(opt => opt.key === status);
        return option?.label || status;
    }

    getStatusColor(status: ActivityStatus | any): string {
        if (!status) return '#6c757d';
        const option = this.statusOptions.find(opt => opt.key === status);
        return option?.color || '#6c757d';
    }

    getActivityColor(activity: any): string {
        if (!activity) return '#6c757d';
        return this.getStatusColor(activity.status);
    }

    getActivityTooltip(activity: any): string {
        if (!activity) return 'Activitate';
        const title = activity.title || 'Activitate';
        const time = activity.start_time || '';
        return title + (time ? ' - ' + time : '');
    }

    getActivityTime(activity: any): string {
        if (!activity?.start_time) return '';
        return this.formatTime(activity.start_time);
    }

    getActivityTitleText(activity: any): string {
        return activity?.title || 'Activitate';
    }

    getActivityType(activity: any): string {
        return activity?.activity_type?.name || '';
    }

    getFormattedTimeRange(activity: any): string {
        if (!activity) return '';
        const startTime = activity.start_time ? this.formatTime(activity.start_time) : '';
        const endTime = activity.end_time ? this.formatTime(activity.end_time) : '';
        return startTime && endTime ? `${startTime} - ${endTime}` : startTime;
    }

    formatTime(timeString: string): string {
        if (!timeString) return '';

        let timeToFormat: string;

        // Handle different time formats from backend
        if (timeString.includes('T') && timeString.includes('Z')) {
            // ISO datetime string like "2025-08-13T07:00:00.000000Z"
            const date = new Date(timeString);
            if (isNaN(date.getTime())) {
                console.error('Invalid datetime string:', timeString);
                return '';
            }
            const hours = date.getHours();
            const minutes = date.getMinutes();
            timeToFormat = `${hours}:${minutes.toString().padStart(2, '0')}`;
        } else if (timeString.includes(':')) {
            // Time string like "07:00" or "07:00:00"
            timeToFormat = timeString;
        } else {
            return timeString;
        }

        const [hours, minutes] = timeToFormat.split(':');
        if (!hours || !minutes) return timeToFormat;

        const formattedHours = parseInt(hours, 10).toString();
        const formattedMinutes = minutes.padStart(2, '0');
        return `${formattedHours}:${formattedMinutes}`;
    }

    getActivitiesForDate(date: Date | string): ScheduledActivity[] {
        let dateString: string;

        if (date instanceof Date) {
            dateString = this.formatDateForAPI(date);
        } else {
            // If it's a day name, convert to date
            if (['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata', 'Duminica'].includes(date)) {
                const dayIndex = this.weekDays.indexOf(date);
                const currentWeekStart = this.getStartOfWeek(this.currentDate);
                const targetDate = new Date(currentWeekStart);
                targetDate.setDate(currentWeekStart.getDate() + dayIndex);
                dateString = this.formatDateForAPI(targetDate);
            } else {
                // It's already a date string
                dateString = date;
            }
        }

        // Use both activities and filteredActivities as fallback
        const activitiesToSearch = this.filteredActivities.length > 0 ? this.filteredActivities : this.activities;

        console.log('getActivitiesForDate:', {
            inputDate: date,
            dateString: dateString,
            totalActivities: activitiesToSearch.length,
            activities: activitiesToSearch,
            activityDates: activitiesToSearch.map(a => a.scheduled_date)
        });

        const matchingActivities = activitiesToSearch.filter(activity => {
            return activity && activity.scheduled_date === dateString;
        }) || [];

        console.log('Found matching activities for', dateString, ':', matchingActivities);
        return matchingActivities;
    }

    getDayOfWeekName(date: Date): string {
        const days = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
        return days[date.getDay()];
    }

    getMonthYearLabel(): string {
        const months = [
            'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
            'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
        ];
        return `${months[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
    }

    // Permission checks
    canCreateActivity(): boolean {
        return this.permissionsService.hasPermission('activity_schedule_create');
    }

    canEditActivity(): boolean {
        return this.permissionsService.hasPermission('activity_schedule_edit');
    }

    canExportActivities(): boolean {
        return this.permissionsService.hasPermission('activity_schedule_export');
    }

    // UI helpers
    toggleFilters(): void {
        this.showFilters = !this.showFilters;
        this.cdr.detectChanges();
    }

    toggleLegend(): void {
        this.showLegend = !this.showLegend;
        this.cdr.detectChanges();
    }

    private checkMobileDevice(): void {
        this.isMobile = window.innerWidth <= 768;
        // Listen for window resize events
        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth <= 768;
            this.cdr.detectChanges();
        });
    }

    // ========================================
    // NOTIFICATION METHODS
    // ========================================

    toggleNotificationsPanel(): void {
        this.showNotificationsPanel = !this.showNotificationsPanel;
    }

    markNotificationAsRead(notificationId: number): void {
        this.activityNotificationService.markAsRead(notificationId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (success) => {
                if (success) {
                    // Notification will be updated via subscription
                }
            },
            error: (error) => {
                console.error('Error marking notification as read:', error);
            }
        });
    }

    markAllNotificationsAsRead(): void {
        if (this.unreadNotificationsCount === 0) return;

        this.activityNotificationService.markAllAsRead().pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (success) => {
                if (success) {
                    this.notificationService.successSwal('Succes', 'Toate notificările au fost marcate ca citite.');
                }
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la marcarea notificărilor.', 'error');
                console.error('Error marking all notifications as read:', error);
            }
        });
    }

    onNotificationClick(notification: ActivityNotification): void {
        // Mark as read
        this.markNotificationAsRead(notification.id);

        // Navigate based on notification type
        if (notification.scheduled_activity_id) {
            // Navigate to the activity date in calendar
            const activity = this.activities.find(a => a.id === notification.scheduled_activity_id);
            if (activity) {
                this.navigateToActivityDate(activity.scheduled_date);

                // Optionally open activity details
                setTimeout(() => {
                    this.openActivityDetails(activity);
                }, 500);
            }
        }
    }

    private navigateToActivityDate(dateString: string): void {
        const date = new Date(dateString);
        this.currentDate = date;
        this.selectedDate = date;
        this.currentView = 'day'; // Switch to day view to show the activity clearly
        this.updateDateRangeForView();
        this.loadCalendarActivities();
    }

    getNotificationIcon(type: string): string {
        return this.activityNotificationService.getNotificationIcon(type);
    }

    formatNotificationDate(dateString: string): string {
        return this.activityNotificationService.formatNotificationDate(dateString);
    }

    isNotificationUnread(notification: ActivityNotification): boolean {
        return this.activityNotificationService.isUnread(notification);
    }

    refreshNotifications(): void {
        this.activityNotificationService.refreshNotifications();
    }

    setupActivityReminders(activityId: number): void {
        this.activityNotificationService.setupActivityReminders(activityId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (notifications) => {
                const count = notifications.length;
                this.notificationService.successSwal(
                    'Succes',
                    `Au fost configurate ${count} mementouri pentru activitate.`
                );
            },
            error: (error) => {
                this.notificationService.warningSwal('Eroare', 'Eroare la configurarea mementouri.', 'error');
                console.error('Error setting up reminders:', error);
            }
        });
    }

    // Helper methods for notifications in template
    getUnreadNotificationsCount(): number {
        return this.unreadNotificationsCount;
    }

    getRecentNotifications(): ActivityNotification[] {
        return this.notifications.slice(0, 5); // Show last 5 notifications
    }

    hasNotifications(): boolean {
        return this.notifications.length > 0;
    }

    // ========================================
    // MISSING TEMPLATE METHODS
    // ========================================

    getActivitiesForDay(day: string): ScheduledActivity[] {
        // Handle both day names and direct dates
        if (this.weekDays.includes(day)) {
            const dayIndex = this.weekDays.indexOf(day);
            const currentWeekStart = this.getStartOfWeek(this.currentDate);
            const targetDate = new Date(currentWeekStart);
            targetDate.setDate(currentWeekStart.getDate() + dayIndex);
            return this.getActivitiesForDate(targetDate);
        }
        return this.getActivitiesForDate(day);
    }

    getActivitiesForHour(hour: number): ScheduledActivity[] {
        const currentDateStr = this.getCurrentDateString();
        return this.filteredActivities.filter(activity => {
            if (!activity.start_time || activity.scheduled_date !== currentDateStr) {
                return false;
            }
            const activityHour = parseInt(activity.start_time.split(':')[0]);
            return activityHour === hour;
        });
    }

    viewActivityDetails(activity: ScheduledActivity): void {
        this.openActivityDetails(activity);
    }

    formatActivityDate(dateString: string): string {
        if (!dateString) return '';

        // Handle ISO datetime strings from backend
        let dateToFormat: Date;
        if (dateString.includes('T') && dateString.includes('Z')) {
            // ISO datetime string like "2025-08-13T07:00:00.000000Z"
            dateToFormat = new Date(dateString);
        } else if (dateString.includes('-')) {
            // Simple date string like "2025-08-13"
            dateToFormat = new Date(dateString + 'T00:00:00');
        } else {
            dateToFormat = new Date(dateString);
        }

        return dateToFormat.toLocaleDateString('ro-RO', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    formatActivityDateShort(dateString: string): string {
        if (!dateString) return '';

        let dateToFormat: Date;

        // Handle different date formats from backend
        if (dateString.includes('T') && dateString.includes('Z')) {
            // ISO datetime string like "2025-08-13T07:00:00.000000Z"
            dateToFormat = new Date(dateString);
        } else if (dateString.includes('-')) {
            // Simple date string like "2025-08-13"
            const [year, month, day] = dateString.split('T')[0].split('-');
            dateToFormat = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
            dateToFormat = new Date(dateString);
        }

        // Check if date is valid
        if (isNaN(dateToFormat.getTime())) {
            console.error('Invalid date string:', dateString);
            return '';
        }

        const day = String(dateToFormat.getDate()).padStart(2, '0');
        const month = String(dateToFormat.getMonth() + 1).padStart(2, '0');
        const year = dateToFormat.getFullYear();
        return `${day}.${month}.${year}`;
    }

    formatActivityDateTime(dateString: string, timeString: string): string {
        if (!dateString) return '';
        const formattedDate = this.formatActivityDateShort(dateString);
        if (!timeString) return formattedDate;

        // Format time from HH:mm to H:i format
        const [hours, minutes] = timeString.split(':');
        const formattedHours = parseInt(hours).toString();
        const formattedTime = `${formattedHours}:${minutes}`;

        return `${formattedDate} ${formattedTime}`;
    }

    selectDate(date: Date): void {
        this.selectedDate = date;
        this.currentDate = date;

        // Get activities for selected date and show them in sidebar
        const activitiesForDate = this.getActivitiesForDate(date);
        console.log('Activities for selected date:', activitiesForDate);

        if (activitiesForDate.length > 0) {
            this.showDayActivitiesInSidebar(date, activitiesForDate);
        } else {
            // No activities for this date, close sidebar if open
            this.closeActivityDetails();
        }
    }

    showDayActivitiesInSidebar(date: Date, activities: ScheduledActivity[]): void {
        this.selectedDateActivities = activities;
        this.selectedDateForSidebar = date;
        this.showDayActivities = true;
        this.showActivityDetails = false; // Close single activity details if open
    }

    toggleStatusFilter(statusKey: string): void {
        const index = this.selectedStatusFilters.indexOf(statusKey);
        if (index > -1) {
            this.selectedStatusFilters.splice(index, 1);
        } else {
            this.selectedStatusFilters.push(statusKey);
        }
        this.applyFilters();
    }

    private getCurrentDateString(): string {
        return this.selectedDate.toISOString().split('T')[0];
    }

    private getStartOfWeek(date: Date): Date {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
        return new Date(d.setDate(diff));
    }

    // TrackBy functions for performance
    trackByWeek(index: number, week: any[]): number {
        return index;
    }

    trackByDay(index: number, day: any): string {
        return day.date ? day.date.toISOString().split('T')[0] : index.toString();
    }

    trackByActivity(index: number, activity: any): number {
        return activity.id || index;
    }

    // ========================================
    // WEEK VIEW METHODS
    // ========================================

    initializeWeekView(): void {
        this.currentWeekStart = this.getStartOfWeek(this.currentDate);
        this.loadWeekActivities();
    }

    loadWeekActivities(): void {
        const weekEnd = new Date(this.currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        this.weekActivities = this.filteredActivities.filter(activity => {
            if (!activity.scheduled_date) return false;
            const activityDate = new Date(activity.scheduled_date);
            return activityDate >= this.currentWeekStart && activityDate <= weekEnd;
        });
    }

    getWeekDayDate(dayIndex: number, asDate: boolean = false): Date | string {
        const date = new Date(this.currentWeekStart);
        date.setDate(date.getDate() + dayIndex);

        if (asDate) return date;

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${day}.${month}`;
    }

    getActivitiesForTimeSlot(dayIndex: number, hour: string): ScheduledActivity[] {
        const targetDate = this.getWeekDayDate(dayIndex, true) as Date;
        const dateString = this.formatDateForAPI(targetDate);
        const hourNumber = parseInt(hour.split(':')[0]);

        return this.weekActivities.filter(activity => {
            if (activity.scheduled_date !== dateString) return false;

            const activityStartHour = this.getHourFromTime(activity.start_time);
            return activityStartHour === hourNumber;
        });
    }

    isCurrentTimeSlot(dayIndex: number, hour: string): boolean {
        const today = new Date();
        const targetDate = this.getWeekDayDate(dayIndex, true) as Date;
        const currentHour = today.getHours();
        const hourNumber = parseInt(hour.split(':')[0]);

        return (
            targetDate.toDateString() === today.toDateString() &&
            currentHour === hourNumber
        );
    }

    createActivityAtTime(date: Date | string, hour: string): void {
        const targetDate = typeof date === 'string' ? new Date(date) : date;
        this.createNewActivity(targetDate);
    }

    // ========================================
    // DAY VIEW METHODS
    // ========================================

    initializeDayView(): void {
        this.loadDayActivities();
    }

    loadDayActivities(): void {
        const dateString = this.formatDateForAPI(this.currentDate);
        this.dayActivities = this.filteredActivities.filter(activity => {
            return activity.scheduled_date === dateString;
        });
    }

    getDayActivities(): ScheduledActivity[] {
        return this.dayActivities;
    }

    getDayActivitiesForTimeSlot(hour: string): ScheduledActivity[] {
        const hourNumber = parseFloat(hour.replace(':', '.'));

        return this.dayActivities.filter(activity => {
            const startHour = this.getDecimalHourFromTime(activity.start_time);
            const endHour = this.getDecimalHourFromTime(activity.end_time);

            return startHour <= hourNumber && hourNumber < endHour;
        });
    }

    isCurrentDayTimeSlot(hour: string): boolean {
        const today = new Date();
        const currentHour = today.getHours() + (today.getMinutes() / 60);
        const slotHour = parseFloat(hour.replace(':', '.'));

        return (
            this.currentDate.toDateString() === today.toDateString() &&
            Math.abs(currentHour - slotHour) < 0.5 // Within 30 minutes
        );
    }

    formatDayViewHeader(date: Date): string {
        const dayName = this.getDayOfWeekName(date);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        return `${dayName}, ${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`;
    }

    previousDay(): void {
        this.currentDate = new Date(this.currentDate);
        this.currentDate.setDate(this.currentDate.getDate() - 1);
        this.loadDayActivities();
    }

    nextDay(): void {
        this.currentDate = new Date(this.currentDate);
        this.currentDate.setDate(this.currentDate.getDate() + 1);
        this.loadDayActivities();
    }

    goToTodayDay(): void {
        this.currentDate = new Date();
        this.loadDayActivities();
    }

    // ========================================
    // HELPER METHODS FOR WEEK/DAY VIEWS
    // ========================================

    private getHourFromTime(timeString: string): number {
        if (!timeString) return 0;

        // Handle ISO datetime strings
        if (timeString.includes('T') && timeString.includes('Z')) {
            const date = new Date(timeString);
            return date.getHours();
        }

        // Handle time strings like "09:30"
        const [hours] = timeString.split(':');
        return parseInt(hours, 10);
    }

    private getDecimalHourFromTime(timeString: string): number {
        if (!timeString) return 0;

        // Handle ISO datetime strings
        if (timeString.includes('T') && timeString.includes('Z')) {
            const date = new Date(timeString);
            return date.getHours() + (date.getMinutes() / 60);
        }

        // Handle time strings like "09:30"
        const [hours, minutes] = timeString.split(':');
        return parseInt(hours, 10) + (parseInt(minutes, 10) / 60);
    }

    formatDateForAPI(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // ========================================
    // ORGANIZATIONAL FILTERING METHODS
    // ========================================

    loadOrganizationalData(): void {
        this.commonService.getDirectiiActive().subscribe({
            next: (directii: any) => {
                this.directii = directii;
                this.filteredDirectii = directii;
                console.log('Loaded directii:', this.directii);
            },
            error: (error) => {
                console.error('Error loading directii:', error);
            }
        });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.filtersForm.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.filtersForm.controls['compartiment_id'].patchValue(-1);
        this.filtersForm.controls['user_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimente = [];
            this.filteredCompartimente = [];
            this.users = [];
            this.filteredUsers = [];
            this.applyFilters();
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsers();
            this.applyFilters();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimente = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.filtersForm.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimente;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.filtersForm.controls['user_id'].patchValue(-1);

        if (compartimentId) {
            this.loadUsers();
        }

        this.applyFilters();
    }

    loadUsers() {
        this.isLoading = true;

        const payload = {
            directie_id: this.filtersForm.get('directie_id')?.value,
            compartiment_id: this.filtersForm.get('compartiment_id')?.value !== -1 ? this.filtersForm.get('compartiment_id')?.value : null
        }

        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.users = [...res];
                    this.filteredUsers = [...res];
                    console.log('USERI: ', res);
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.users.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.filtersForm.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.users;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onUserChange(): void {
        this.applyFilters();
    }

    viewUserCalendar(): void {
        const selectedUserId = this.filtersForm.get('user_id')?.value;
        if (selectedUserId) {
            // Apply user filter and show their calendar
            this.applyFilters();

            // Switch to week or day view for better user-specific viewing
            if (this.currentView === 'month') {
                this.switchView('week');
            }

            // Show notification about filtering
            const selectedUser = this.filteredUsers.find(u => u.id_user === selectedUserId);
            if (selectedUser) {
                this.notificationService.successSwal(
                    'Calendar Filtrat',
                    `Se afișează calendarul pentru ${selectedUser.full_name}`
                );
            }
        }
    }

    clearOrganizationalFilters(): void {
        this.filtersForm.patchValue({
            directie_id: -1,
            compartiment_id: -1,
            user_id: -1
        });

        this.compartimente = [];
        this.filteredCompartimente = [];
        this.users = [];
        this.filteredUsers = [];

        this.applyFilters();
    }

    onDateFilterChange(): void {
        this.applyFilters();
    }

    // ========================================
    // EXPORT FUNCTIONALITY METHODS
    // ========================================

    exportActivities(format: 'pdf' | 'csv'): void {
        if (this.filteredActivities.length === 0) {
            this.notificationService.warningSwal('Export', 'Nu sunt activități pentru export');
            return;
        }

        const exportData = {
            format: format,
            activities: this.filteredActivities.map(activity => ({
                id: activity.id,
                title: activity.title,
                scheduled_date: activity.scheduled_date,
                start_time: activity.start_time,
                end_time: activity.end_time,
                status: activity.status,
                activity_type: activity.activity_type?.name || 'N/A',
                location: activity.location || '',
                assigned_user_id: activity.assigned_user_id,
                notes: activity.notes || '',
                duration_minutes: activity.duration_minutes
            })),
            filters: {
                start_date: this.dateFilter.startDate ? this.formatDateForAPI(this.dateFilter.startDate) : null,
                end_date: this.dateFilter.endDate ? this.formatDateForAPI(this.dateFilter.endDate) : null,
                user_id: this.filtersForm.get('user_id')?.value,
                directie_id: this.filtersForm.get('directie_id')?.value,
                compartiment_id: this.filtersForm.get('compartiment_id')?.value,
                status_filters: this.selectedStatusFilters,
                search_term: this.searchTerm
            },
            export_type: 'all_activities',
            generated_by: this.currentUserId,
            generated_at: new Date().toISOString()
        };

        this.performExport(exportData, format, 'activitati_programate');
    }

    exportCurrentDay(format: 'pdf' | 'csv'): void {
        if (this.currentView !== 'day' || this.getDayActivities().length === 0) {
            this.notificationService.warningSwal('Export', 'Nu sunt activități pentru ziua curentă');
            return;
        }

        const dayActivities = this.getDayActivities();
        const dateString = this.formatDateForAPI(this.currentDate);

        const exportData = {
            format: format,
            activities: dayActivities.map(activity => ({
                id: activity.id,
                title: activity.title,
                scheduled_date: activity.scheduled_date,
                start_time: activity.start_time,
                end_time: activity.end_time,
                status: activity.status,
                activity_type: activity.activity_type?.name || 'N/A',
                location: activity.location || '',
                assigned_user_id: activity.assigned_user_id,
                notes: activity.notes || '',
                duration_minutes: activity.duration_minutes
            })),
            export_type: 'single_day',
            export_date: dateString,
            export_date_formatted: this.formatDayViewHeader(this.currentDate),
            generated_by: this.currentUserId,
            generated_at: new Date().toISOString()
        };

        this.performExport(exportData, format, `activitati_${dateString}`);
    }

    exportCurrentWeek(format: 'pdf' | 'csv'): void {
        if (this.currentView !== 'week' || this.weekActivities.length === 0) {
            this.notificationService.warningSwal('Export', 'Nu sunt activități pentru săptămâna curentă');
            return;
        }

        const weekStart = this.formatDateForAPI(this.currentWeekStart);
        const weekEnd = new Date(this.currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const weekEndString = this.formatDateForAPI(weekEnd);

        const exportData = {
            format: format,
            activities: this.weekActivities.map(activity => ({
                id: activity.id,
                title: activity.title,
                scheduled_date: activity.scheduled_date,
                start_time: activity.start_time,
                end_time: activity.end_time,
                status: activity.status,
                activity_type: activity.activity_type?.name || 'N/A',
                location: activity.location || '',
                assigned_user_id: activity.assigned_user_id,
                notes: activity.notes || '',
                duration_minutes: activity.duration_minutes
            })),
            export_type: 'week_range',
            week_start: weekStart,
            week_end: weekEndString,
            week_formatted: `Săptămâna ${weekStart} - ${weekEndString}`,
            generated_by: this.currentUserId,
            generated_at: new Date().toISOString()
        };

        this.performExport(exportData, format, `activitati_saptamana_${weekStart}`);
    }

    exportSingleActivity(activity: ScheduledActivity, format: 'pdf' | 'csv'): void {
        const exportData = {
            format: format,
            activities: [{
                id: activity.id,
                title: activity.title,
                scheduled_date: activity.scheduled_date,
                start_time: activity.start_time,
                end_time: activity.end_time,
                status: activity.status,
                activity_type: activity.activity_type?.name || 'N/A',
                location: activity.location || '',
                assigned_user_id: activity.assigned_user_id,
                notes: activity.notes || '',
                duration_minutes: activity.duration_minutes,
                description: activity.description || ''
            }],
            export_type: 'single_activity',
            activity_id: activity.id,
            generated_by: this.currentUserId,
            generated_at: new Date().toISOString()
        };

        this.performExport(exportData, format, `activitate_${activity.id}`);
    }

    private performExport(exportData: any, format: 'pdf' | 'csv', filename: string): void {
        // Show loading state
        this.isLoading = true;

        this.activityScheduleService.exportActivities(exportData).subscribe({
            next: (response: any) => {
                this.isLoading = false;

                if (response && response.success) {
                    // Handle file download
                    if (response.download_url) {
                        // If backend provides download URL
                        window.open(response.download_url, '_blank');
                    } else if (response.file_content) {
                        // If backend returns file content directly
                        this.downloadFile(response.file_content, `${filename}.${format}`, format);
                    } else if (response.data) {
                        // If backend returns raw data, create file client-side
                        if (format === 'csv') {
                            this.createAndDownloadCSV(exportData.activities, filename);
                        }
                    }

                    this.notificationService.successSwal(
                        'Export',
                        `Export ${format.toUpperCase()} a fost generat cu succes`
                    );
                } else {
                    throw new Error(response.message || 'Export failed');
                }
            },
            error: (error) => {
                this.isLoading = false;
                console.error('Export error:', error);

                // Fallback to client-side CSV generation if backend fails
                if (format === 'csv') {
                    try {
                        this.createAndDownloadCSV(exportData.activities, filename);
                        this.notificationService.successSwal('Export', 'CSV generat local cu succes');
                    } catch (csvError) {
                        this.notificationService.warningSwal('Export', 'Eroare la generarea exportului');
                    }
                } else {
                    this.notificationService.warningSwal(
                        'Export',
                        error.error?.message || 'Eroare la generarea exportului'
                    );
                }
            }
        });
    }

    private downloadFile(fileContent: string, filename: string, format: string): void {
        const mimeType = format === 'csv' ? 'text/csv;charset=utf-8' : 'application/pdf';

        let blob: Blob;

        if (format === 'csv') {
            // Create blob with UTF-8 BOM for proper Romanian character support
            const utf8BOM = '\uFEFF';
            const content = fileContent.startsWith('\uFEFF') ? fileContent : utf8BOM + fileContent;
            blob = new Blob([content], { type: mimeType });
        } else if (format === 'pdf') {
            // Handle base64 encoded PDF content
            try {
                const binaryString = atob(fileContent);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                blob = new Blob([bytes], { type: mimeType });
            } catch (error) {
                console.error('Error decoding base64 PDF:', error);
                throw new Error('Failed to decode PDF content');
            }
        } else {
            // Fallback for other formats
            blob = new Blob([fileContent], { type: mimeType });
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    private createAndDownloadCSV(activities: any[], filename: string): void {
        const headers = [
            'ID',
            'Titlu',
            'Data',
            'Ora început',
            'Ora sfârșit',
            'Status',
            'Tip activitate',
            'Locație',
            'Utilizator asignat',
            'Durată (min)',
            'Observații'
        ];

        const csvContent = [
            headers.join(','),
            ...activities.map(activity => [
                activity.id,
                `"${activity.title || ''}"`,
                activity.scheduled_date,
                activity.start_time,
                activity.end_time,
                activity.status,
                `"${activity.activity_type || ''}"`,
                `"${activity.location || ''}"`,
                activity.assigned_user_id || '',
                activity.duration_minutes || '',
                `"${(activity.notes || '').replace(/"/g, '""')}"`
            ].join(','))
        ].join('\n');

        this.downloadFile(csvContent, `${filename}.csv`, 'csv');
    }
}